// ============================================================
// TERROR TCG — THE NECRO SEAL (card identity layer)
// ============================================================
// July 27, 2026 — caP'n's ruling: Resurrection tallies are PER COPY.
//
// THE PROBLEM THIS SOLVES
// ----------------------
// A player's Necropolis is a flat array of card-id strings. Two ruined
// copies of the same Beb land there as two IDENTICAL strings:
//
//     necropolis: ["taez_beb", "taez_beb"]
//
// Nothing tells them apart. So the old tally counted by NAME and capped
// an entire playset at one card's worth of returns — and worse, the
// resurrect write `necropolis.filter(id => id !== cardId)` deleted BOTH
// copies when you revived one.
//
// caP'n, July 27 2026: "you could even have doubles right from the same
// pack... we have to keep track of that specific card. Has to be per copy
// of that specific card, regardless of it being a copy of another, so we
// can get a lot of resurrections from just a few bebs and champions."
//
// THE SEAL
// --------
// The instant a being is ruined, it is SEALED — stamped with an identity
// it keeps for the rest of the Solrend:
//
//     taez_beb              →  taez_beb~0~7
//     └ base id                └ base  │  └ seq (unique per player)
//                                      └ graveshift generation
//
// Three properties make this cheap:
//
//   1. ONLY ESCENDANTS ARE SEALED. Tomes, Impera, U'hltis, Affinities,
//      Esclings and Prophecies never carry a tag — they have no tally.
//      Blast radius stays inside the one system that needs it.
//
//   2. SEALED ONCE, KEPT FOREVER. A sealed being carries its tag onto the
//      field when Resurrected, into a Soul when Ascended over, and back to
//      the Necropolis when ruined again. Re-ruining does NOT re-seal, so
//      the tally follows the physical copy for its whole life. That is the
//      entire trick — mint-on-every-ruin would reset the count and hand
//      out infinite returns.
//
//   3. BARE IDS STILL WORK. Everything untagged behaves exactly as before
//      (gen 0, no tally history). There is no migration: a match already
//      in flight keeps running, and its beings seal the next time they
//      are ruined.
//
// SEPARATOR CHOICE — "~", AND WHY NOT "#"
// ---------------------------------------
// Firebase RTDB forbids . $ # [ ] / in KEYS. Graveshift folds the
// Necropolis into the Grimoire, which is a `{cardId: count}` MAP — so a
// tagged id becomes a Firebase KEY on that path. "#" would have thrown on
// the first Graveshift after a ruin. "~" is legal in both keys and values.
// (Graveshift strips tags anyway — see sealGraveshift — but the separator
// stays key-safe so a stray tag can never break a write.)
//
// THE GRAVESHIFT GENERATION
// -------------------------
// caP'n, July 27 2026: "after a graveshift, none of those cards can be
// resurrected using the passive rule, only through tomes, imperas,
// primeras, etc."   (VERBATIM — and "primeras" here is the STAMP, not
// the card: the quote lists Tomes, Impera and the stamps side by side,
// which is exactly R89's exemption list. Not swept by the Impera rename.)
//
// The middle segment is that rule, carried on the card. Cards sealed
// before any Graveshift are gen 0 and resurrect normally. After a
// Graveshift the player's generation ticks up, and everything sealed from
// then on is gen >= 1 — barred from the paid Necropolis Resurrect, still
// fully reachable by card effects (Time Primera, the Nihil Tomes, any
// future Impera that returns a being).
//
// Beings STANDING ON THE FIELD during a Graveshift did not shift, so
// sealGraveshift stamps them at the OUTGOING generation before the tick.
// That is why the stamp pass exists: without it, a Lord that survived the
// shift would seal at the new generation when it finally fell and be
// wrongly denied its return.
//
// Consumed by: Shatterfield.js · NPC.js · RulesEnforcer.js
// ============================================================

export const TAG_SEP = "~";

/** The catalog id, with any seal stripped. Safe on untagged ids and null. */
export const baseId = (id) =>
  typeof id === "string" && id.includes(TAG_SEP) ? id.split(TAG_SEP)[0] : id;

/** Is this id sealed? */
export const isSealed = (id) =>
  typeof id === "string" && id.includes(TAG_SEP);

/** Graveshift generation carried by a seal. Untagged / malformed => 0. */
export const tagGen = (id) => {
  if (!isSealed(id)) return 0;
  const n = parseInt(id.split(TAG_SEP)[1], 10);
  return Number.isFinite(n) ? n : 0;
};

/** Per-player sequence number carried by a seal. Untagged => 0. */
export const tagSeq = (id) => {
  if (!isSealed(id)) return 0;
  const n = parseInt(id.split(TAG_SEP)[2], 10);
  return Number.isFinite(n) ? n : 0;
};

/** Build a seal. */
export const mintTag = (base, gen, seq) =>
  `${baseId(base)}${TAG_SEP}${gen}${TAG_SEP}${seq}`;

// ── SEQUENCE HIGH-WATER ─────────────────────────────────────
// No counter field is stored. Sealed cards live in exactly FIVE places —
// hand, field escendants, field souls, Necropolis, and (since R112, the
// Exile rift) exile — so the highest seq across those five IS the
// high-water mark for every seal that exists.
//
// ⚠ THE EXILE RIFT WAS THE FIFTH PLACE AND ADDING IT IS NOT OPTIONAL.
// This scan is what stops two physical copies colliding on one identity.
// A sealed being sitting in the Exile rift and NOT scanned here is
// invisible to the high-water mark, so the next mint reuses its seq — and
// two different cards then share a tally. That is silent per-copy tally
// corruption: no error, no wrong-looking board, just a Beb that quietly
// gets six returns instead of three. The blast radius of this module was
// deliberately kept to four places; the Exile rift genuinely widens it to
// five, and every place it widens to has to be listed HERE or the whole
// device stops working.
// Scanning is O(cards in play), which is a few dozen.
//
// Extra arrays (an in-flight payload not yet written) are folded in so a
// single write that seals several beings at once never collides with
// itself.
export const seqHighWater = (player, ...extraArrays) => {
  let max = 0;
  const scan = (arr) => {
    (arr || []).forEach((id) => {
      const n = tagSeq(id);
      if (n > max) max = n;
    });
  };
  scan(player?.hand);
  scan(player?.necropolis);
  scan(player?.exile);          // R112 — the fifth place a sealed id can live
  (player?.field?.esclings || []).forEach((s) => {
    if (!s) return;
    const n = tagSeq(s.escendant);
    if (n > max) max = n;
    scan(s.soul);
  });
  extraArrays.forEach(scan);
  return max;
};

// ── THE SEAL PASS ───────────────────────────────────────────
// Given an outgoing `necropolis` array and the live player, return a new
// array in which every previously-unsealed ESCENDANT has been stamped.
// Already-sealed entries pass through untouched — that is what preserves
// a copy's tally across repeated ruin/resurrect cycles.
//
// `isEscendant` is injected so this module never imports the card
// catalog (Shatterfield and NPC each have their own getCard).
export const sealNecropolis = (necropolis, player, isEscendant) => {
  if (!Array.isArray(necropolis)) return necropolis;
  const gen = player?.gshift || 0;
  let seq = seqHighWater(player, necropolis);
  return necropolis.map((id) => {
    if (!id || isSealed(id)) return id;
    if (!isEscendant(id)) return id; // only beings carry a tally
    seq += 1;
    return mintTag(id, gen, seq);
  });
};

// ── THE WRITE OVERSEER ──────────────────────────────────────
// Wraps an updatePlayer payload. If it carries a `necropolis` array, that
// array is sealed first. Every ruin path in the game — 58 call sites in
// Shatterfield alone, plus the NPC's — funnels through updatePlayer, so
// sealing here covers all of them without touching a single call site.
export const sealPayload = (updates, player, isEscendant) => {
  if (!updates || !Array.isArray(updates.necropolis)) return updates;
  return { ...updates, necropolis: sealNecropolis(updates.necropolis, player, isEscendant) };
};

// ── GRAVESHIFT ──────────────────────────────────────────────
// R42 folds hand + Necropolis into a fresh Grimoire MAP. Two things must
// happen in that one write:
//
//   1. STAMP THE SURVIVORS. Beings standing on the field (and lower forms
//      sitting in their Souls) did not go through the shift, so they are
//      sealed HERE at the outgoing generation. Anything sealed after this
//      point is, by definition, a card that shifted.
//
//   2. STRIP AND TICK. Tags are removed as cards fold into the Grimoire
//      map — keys stay bare, so no key ever contains a separator and the
//      map does not fragment (3 copies of a Beb stay `taez_beb: 3` rather
//      than three single-count keys). The generation then ticks, and the
//      next time any of those cards is ruined it seals at gen >= 1 and is
//      barred from the passive Resurrect.
//
// Returns { grimoire, field, gshift } to fold into the Graveshift write.
export const sealGraveshift = (player, isEscendant) => {
  const gen = player?.gshift || 0;
  let seq = seqHighWater(player);

  // 1 — stamp field survivors at the OUTGOING generation
  const field = JSON.parse(JSON.stringify(player?.field || {}));
  (field.esclings || []).forEach((slot) => {
    if (!slot) return;
    if (slot.escendant && !isSealed(slot.escendant) && isEscendant(slot.escendant)) {
      seq += 1;
      slot.escendant = mintTag(slot.escendant, gen, seq);
    }
    if (Array.isArray(slot.soul)) {
      slot.soul = slot.soul.map((id) => {
        if (!id || isSealed(id) || !isEscendant(id)) return id;
        seq += 1;
        return mintTag(id, gen, seq);
      });
    }
  });

  // 2 — fold hand + Necropolis into the new Grimoire, tags stripped
  const grimoire = {};
  [...(player?.hand || []), ...(player?.necropolis || [])].forEach((id) => {
    if (!id) return;
    const b = baseId(id);
    grimoire[b] = (grimoire[b] || 0) + 1;
  });

  return { grimoire, field, gshift: gen + 1 };
};

// ── THE TALLY ───────────────────────────────────────────────
// `resurrectedCards` is append-only and EVERY resurrection method appends
// (R44). Counting exact-tag occurrences IS the per-copy tally: two sealed
// copies of the same Beb hold different tags and therefore different
// counts, which is the whole point of this file.
//
// Unsealed ids fall back to name matching — the pre-seal behaviour, so a
// match already in flight is never broken by the upgrade.
export const resTally = (resurrectedCards, id) => {
  if (!id) return 0;
  const list = resurrectedCards || [];
  if (isSealed(id)) return list.filter((x) => x === id).length;
  return list.filter((x) => baseId(x) === baseId(id)).length;
};

/**
 * R85 — the passive (paid) Necropolis Resurrect is closed to anything that
 * has been through a Graveshift. Card effects are NOT gated by this and
 * must never call it.
 */
export const canPassiveResurrect = (id) => tagGen(id) === 0;
